/**
 * The other parts of this revision, and the way between them.
 *
 * A CONTROLLED DOCUMENT IS SEVERAL DOCUMENTS. A procedure has appendices, a training summary, a
 * form; QMS shows them as cards on the record and this is the same set, from inside one of them.
 * Without it the editor could open a part and never admit the others existed -- the URL named one,
 * and nothing on the page said it was one of four.
 *
 * ORDERED AS EVERYTHING ELSE ORDERS THEM -- SEQUENCE then name. The running order of a controlled
 * document's parts is part of what gets signed, so the editor must not invent its own.
 *
 * ADD AND DELETE LIVE HERE because this is where somebody knows what the document still needs.
 * Both are refused by the server on a revision that is closed; the controls are hidden rather than
 * offered-and-refused, and hiding them is not the enforcement.
 */
import React from "react";
import {
    Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Button, IconButton, Tooltip,
} from "@mui/material";
import { AddOutlined, DeleteOutlined, DescriptionOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { makeCresAxios } from "../../util/CresAxios";
import useAppContext from "../../util/AppContext";
import { Grey3, Grey5, Grey17, Blue3 } from "../../theme/styles";

export type Part = {
    USID: string;
    Name: string;
    Sequence: number | null;
    Origin: string;
    OpenSuggestions: number;
    HasBody: boolean;
    Characters: number;
};

export default function DocumentParts({ revisionUSID, documentUSID, editable, reloadKey,
                                        deleteRequest, onCount, onChanged }: {
    revisionUSID: string;
    documentUSID: string;
    editable: boolean;
    /** Bumped by the editor when a rename lands, so the strip reflects it without a page reload. */
    reloadKey: number;
    /**
     * Bumped by File > Delete.
     *
     * THE MENU ASKS; THIS COMPONENT STILL DECIDES AND STILL CONFIRMS. The confirmation names what
     * is being lost -- "this removes 1,430 characters" -- and that needs the part's own row, which
     * is here. Giving the File menu its own copy of that dialog would be two dialogs to keep
     * honest, and the one on the menu would be the one that drifted.
     */
    deleteRequest: number;
    /** So the File menu can tell whether deleting is even possible. */
    onCount: (count: number) => void;
    onChanged: () => void;
}) {
    const sessionContext = useAppContext()?.sessionContext as any;
    const navigate = useNavigate();
    const axios = React.useMemo(() => makeCresAxios(sessionContext), [sessionContext]);

    const [parts, setParts] = React.useState<Part[] | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [problem, setProblem] = React.useState<string | null>(null);
    const [confirming, setConfirming] = React.useState<Part | null>(null);

    const load = React.useCallback(async () => {
        try {
            const response = await axios.get(
                `/t11/v1/document_contents?RevisionUSID=${encodeURIComponent(revisionUSID)}`);
            const found = response?.data?.Results ?? [];
            setParts(found);
            onCount(found.length);
        } catch {
            // A STRIP THAT CANNOT LOAD SHOWS NOTHING, not an error over the document. The editor
            // below it is working; the only thing lost is the way to the other parts.
            setParts([]);
            onCount(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [axios, revisionUSID]);

    React.useEffect(() => { load(); }, [load, reloadKey]);

    // The first render carries deleteRequest 0 and must not open a dialog; only a CHANGE is an ask.
    const asked = React.useRef(deleteRequest);
    React.useEffect(() => {
        if (deleteRequest === asked.current) return;
        asked.current = deleteRequest;
        setConfirming((parts ?? []).find(one => one.USID === documentUSID) ?? null);
    }, [deleteRequest, parts, documentUSID]);

    const add = async () => {
        setBusy(true);
        try {
            const made = (await axios.post("/t11/v1/document_contents", { RevisionUSID: revisionUSID })).data;
            await load();
            navigate(`/document/${revisionUSID}/${made.USID}`);
            onChanged();
            setProblem(null);
        } catch (error: any) {
            setProblem(error?.response?.data?.Cause || "That document could not be created.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (part: Part) => {
        setConfirming(null);
        setBusy(true);
        try {
            await axios.delete(`/t11/v1/document_contents?USID=${encodeURIComponent(part.USID)}`);
            const left = (parts ?? []).filter(one => one.USID !== part.USID);
            await load();
            // DELETING THE ONE YOU ARE IN leaves nowhere to stand, so move to its neighbour --
            // or back to the list when it was the last of them.
            if (part.USID === documentUSID) {
                if (left[0]) navigate(`/document/${revisionUSID}/${left[0].USID}`);
                else navigate("/");
            }
            onChanged();
            setProblem(null);
        } catch (error: any) {
            setProblem(error?.response?.data?.Cause || "That document could not be deleted.");
        } finally {
            setBusy(false);
        }
    };

    if (parts === null) {
        return <Box sx={{ display: "flex", py: 1 }}><CircularProgress size={16}/></Box>;
    }

    // ONE PART AND NOTHING TO ADD IS NOTHING TO CHOOSE, so the strip stays out of the way --
    // a tab bar with a single tab describes the only thing its owner can look at.
    if (parts.length <= 1 && !editable) return null;

    return (
        <Box sx={{ mb: 2 }}>
            {problem && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setProblem(null)}>{problem}</Alert>}

            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
                {parts.map(part => {
                    const here = part.USID === documentUSID;
                    return (
                        <Chip key={part.USID} size="small" clickable={!here}
                              aria-label={`Open ${part.Name}`}
                              aria-current={here ? "true" : undefined}
                              onClick={here ? undefined
                                            : () => navigate(`/document/${revisionUSID}/${part.USID}`)}
                              icon={<DescriptionOutlined sx={{ fontSize: 15 }}/>}
                              label={
                                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                      {part.Name}
                                      {/* EMPTY IS WORTH SAYING. A part somebody created and never
                                          wrote in looks identical to one that is finished, from a
                                          list of names. */}
                                      {!part.HasBody && (
                                          <Box component="span" sx={{ fontSize: 10, color: Grey5 }}>empty</Box>)}
                                      {!!part.OpenSuggestions && (
                                          <Box component="span" sx={{ fontSize: 10, color: Grey5 }}>
                                              {part.OpenSuggestions} open
                                          </Box>)}
                                  </Box>}
                              sx={{ height: 26, fontSize: 12, borderRadius: 999,
                                    border: `1px solid ${here ? Blue3 : Grey3}`,
                                    backgroundColor: here ? "#E8F0FE" : "#FFFFFF",
                                    color: here ? Grey17 : Grey17, fontWeight: here ? 600 : 400 }}/>
                    );
                })}

                {editable && (
                    <Tooltip title="Add another document to this revision">
                        <span>
                            <IconButton size="small" onClick={add} disabled={busy} aria-label="Add document"
                                        sx={{ border: `1px dashed ${Grey3}`, borderRadius: 999, p: 0.4 }}>
                                <AddOutlined sx={{ fontSize: 16 }}/>
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                <Box sx={{ flexGrow: 1 }}/>

                {/* DELETING IS OFFERED ONLY FOR THE ONE YOU ARE LOOKING AT. A row of bins beside a
                    row of names is an invitation to remove the wrong one, and a part of a
                    controlled document is not something to delete by near miss. */}
                {editable && parts.length > 1 && (
                    <Tooltip title="Delete this document">
                        <span>
                            <IconButton size="small" disabled={busy} aria-label="Delete this document"
                                        onClick={() => setConfirming(parts.find(p => p.USID === documentUSID) ?? null)}>
                                <DeleteOutlined sx={{ fontSize: 17, color: "#C0392B" }}/>
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>

            {/* ASKED, AND THE QUESTION NAMES WHAT IS BEING LOST. Deleting text somebody wrote is
                not undoable here -- the audit log records that it happened and by whom, which is
                the regulatory requirement, and is not a way to get the words back. */}
            <Dialog open={!!confirming} onClose={() => setConfirming(null)}>
                <DialogTitle sx={{ fontSize: 17 }}>Delete “{confirming?.Name}”?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 14 }}>
                        {confirming?.Characters
                            ? `This removes ${confirming.Characters.toLocaleString()} characters of text from this revision. It cannot be undone — the audit trail will record the deletion, not the words.`
                            : "This document has nothing written in it yet."}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(null)}>Keep it</Button>
                    <Button color="error" onClick={() => confirming && remove(confirming)}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
